import { useState, useRef, useCallback } from 'react';
import classNames from 'classnames';

import { isSvpCategory } from '../../../../modules/helpers';
import { getCategoryVerticalImage } from '../../../../shared/helpers/categoryHelpers';
import { getAssetVerticalImage } from '../../../../shared/helpers/assetHelpers';
import { MobileCard } from '../MobileCard/MobileCard';
import { getStyleProperty } from '../../../../utils/getStyleProperty';
import { getImageSources } from '../../../../helpers/images';
import { ComponentLink } from '../../../ComponentLink/ComponentLink';

import type { Vendor } from '../../../../types';
import type { SvpAssetAdditional } from '@vgtv/api-client/lib/svp_asset';
import type { SvpCategoryAdditional } from '@vgtv/api-client/lib/svp_category';
import type { FC } from 'react';

import * as styles from './MobileSlider.module.scss';

interface MobileSliderProps {
  items: (SvpAssetAdditional | SvpCategoryAdditional)[];
  onClick: (index: number) => void;
  vendor: Vendor;
}

export const MobileSlider: FC<MobileSliderProps> = ({
  items,
  onClick,
  vendor,
}) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const scrollRef = useRef<HTMLUListElement>(null);

  const scrollToItem = useCallback((index) => {
    const paddingValue = parseFloat(
      getStyleProperty('--mobile-carousel-item-padding')
    );
    const cardWidth = parseFloat(
      getStyleProperty('--mobile-carousel-item-width')
    );

    if (scrollRef.current) {
      scrollRef.current.scrollTo(
        paddingValue +
          (index * (cardWidth + paddingValue) -
            window.screen.width / 2 +
            cardWidth / 2),
        0
      );
      setSelectedIndex(index);
      onClick(index);
    }
  }, []);

  const handleCardClicked = (index: number) => {
    scrollToItem(index);
  };

  return (
    <ul className={styles.carousel} ref={scrollRef}>
      {items.map((item, index) => {
        const verticalImageSources = getImageSources({
          imageUrl: isSvpCategory(item)
            ? getCategoryVerticalImage(item)
            : getAssetVerticalImage(item),
          aspectRatio: 'vertical',
          imagePreset: 'assetsSliderVertical',
        });

        const children = (
          <li
            aria-hidden
            className={classNames(styles.item, {
              [styles.selectedItem]: index === selectedIndex,
            })}
            onClick={() => handleCardClicked(index)}
          >
            <MobileCard
              imageUrl={verticalImageSources.src}
              name={item.title}
              isSelected={index === selectedIndex}
            />
          </li>
        );

        return (
          <ComponentLink
            disabled={index !== selectedIndex}
            item={item}
            vendor={vendor}
            key={
              isSvpCategory(item) ? `category-${item.id}` : `asset-${item.id}`
            }
          >
            {children}
          </ComponentLink>
        );
      })}
    </ul>
  );
};
